import React, { useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { GlobalHotKeys } from 'react-hotkeys';
import cx from 'classnames';

import styled, { withTheme } from 'styled-components';
import {
  Tooltip,
  ButtonBaseProps,
  Badge,
  BadgeProps,
  Menu,
  MenuItem,
  Divider,
  Popper,
  Paper,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import SyncIcon from '@material-ui/icons/Sync';
import SpeakerNotesOffIcon from '@material-ui/icons/SpeakerNotesOff';
import EventIcon from '@material-ui/icons/Event';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';

import { omit } from 'lodash';

import { ReactComponent as MarkEmailReadIcon } from 'assets/images/mark_email_read.svg';

import IconBorder from 'components/shared/IconBorder';
import { StyledIconButton } from 'components/shared/MuiExtensions';

import {
  apiContinueAutomation,
  apiCreateScheduledFollowUp,
  apiScheduleCall,
  apiDeleteScheduledFollowUp,
  apiEscalateLead,
  apiInvalidPhoneLead,
  apiNoResponseNeeded,
  apiStopContactLead,
} from 'state/lead.state';
import { RootState } from 'state/store';
import {
  AppCopyText,
  LeadStatus,
  LeadUpdateActions,
  Colors,
  ObjectLiteral,
  CommunicationTypes,
  ScheduleCallPayload,
  ScheduleFollowUpTimeFrames,
} from 'typings';
import {
  isCallCenterTransferRestricted,
  isWithinBusinessHours,
  isTeamHoliday,
} from 'utils/functions';

import ScheduleFollowUpForm from './ScheduleFollowUpForm';
import StopContactDialog from './StopContactDialog';

interface LeadActionButtonProps extends ButtonBaseProps {
  icon: React.ReactNode;
  title: string;
  showBadge?: boolean;
}

interface ConditionalBadgeProps extends BadgeProps {
  show: boolean;
}

const ConditionalBadge = ({
  show,
  children,
  ...props
}: React.PropsWithChildren<ConditionalBadgeProps>) => {
  if (!show) return <>{children}</>;

  return <Badge {...props}>{children}</Badge>;
};

const StyledIconBorder = styled(withTheme(IconBorder))((props) => ({
  backgroundColor: props?.disabled
    ? props.theme.palette.background.disabled
    : props.theme.palette.primary.main,
}));

const LeadActionButton = ({ icon, title, showBadge = false, ...props }: LeadActionButtonProps) => {
  return (
    <div className="full-width">
      <Tooltip title={title} placement="top" arrow>
        <span>
          <StyledIconButton className="full-width" color={Colors.BLACK} {...props}>
            <ConditionalBadge show={showBadge} color="secondary" badgeContent={1}>
              <StyledIconBorder boxShadow disabled={props?.disabled} fill={Colors.WHITE}>
                {icon}
              </StyledIconBorder>
            </ConditionalBadge>
          </StyledIconButton>
        </span>
      </Tooltip>
    </div>
  );
};

const LeadActionsWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  position: relative;
  padding: ${(props) => props.theme.spacing(1.5, 0)};
  background: ${(props) => props.theme.palette.background.alternative};

  > * + * {
    border-left: 2px solid ${Colors.TRANSPARENT_BLACK};
  }
`;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    chip: {
      background: theme.palette.background.default,
      flexDirection: 'row-reverse',
      boxShadow: theme.shadows[6],
      textAlign: 'center',
      justifyContent: 'stretch',
    },
    chipStack: {
      position: 'absolute',
      display: 'flex',
      flexDirection: 'column',
      bottom: theme.spacing(7.5),
      right: theme.spacing(0),
    },
    chipStackWrapper: {
      position: 'relative',
    },
    customSvgIcon: {
      width: theme.spacing(2.5),
      height: theme.spacing(2.5),
    },
  })
);

const LeadActions = () => {
  const dispatch = useDispatch();
  const lead = useSelector((state: RootState) => {
    const { id, phone, email, timezone, requestedCallback, requestedFollowUp, status, agent } =
      state.lead.entity || {};
    return { id, phone, email, timezone, requestedCallback, requestedFollowUp, status, agent };
  }, shallowEqual);
  const isCallCenterRoutingEnabled = useSelector(
    (state: RootState) =>
      !!state.lead.entity?.agent?.agentSetting?.settings?.inboundCalls?.callCenterRouting?.enabled
  );
  const conciergeIsOnCall = useSelector((state: RootState) => state.concierge.isOnCall);
  const conciergeLdeIsLimited = useSelector(
    (state: RootState) => state.concierge.entity?.ldeIsLimited
  );

  const leadIsOpen = lead.status === LeadStatus.NEW_LEAD;
  const leadHasFurOrRcb = !!lead.requestedFollowUp || !!lead.requestedCallback;
  const disallowContinueAuto = !leadIsOpen || leadHasFurOrRcb;
  const disallowNoResponseNeeded = leadIsOpen && !leadHasFurOrRcb;

  const canCallNow =
    lead?.agent &&
    (!isCallCenterRoutingEnabled ||
      !isCallCenterTransferRestricted(lead.agent) ||
      (!isTeamHoliday(lead.agent) && isWithinBusinessHours(lead.agent)));

  const scheduledLeadType = lead.requestedFollowUp
    ? 'follow-up'
    : lead.requestedCallback
      ? 'scheduled call'
      : '';

  const scheduleFollowUpTrigger: any = useRef();
  const [showScheduleFollowUpActions, setShowScheduleFollowUpActions] = useState(false);
  const [openScheduleFollowUpDialog, setOpenScheduleFollowUpDialog] = useState(false);
  const [scheduleFollowUpTimeFrame, setScheduleFollowUpTimeFrame] = useState(
    ScheduleFollowUpTimeFrames.CUSTOM
  );
  const [openStopContactDialog, setOpenStopContactDialog] = useState(false);
  const classes = useStyles();

  const openScheduleFollowUpFormDialog = (timeFrame: ScheduleFollowUpTimeFrames) => () => {
    setOpenScheduleFollowUpDialog(true);
    setShowScheduleFollowUpActions(false);
    setScheduleFollowUpTimeFrame(timeFrame);
  };

  const closeScheduleFollowUpDialog = () => {
    setOpenScheduleFollowUpDialog(false);
  };

  const toggleScheduleFollowUpActions = () => {
    if (openScheduleFollowUpDialog) {
      setOpenScheduleFollowUpDialog(false);
    }

    setShowScheduleFollowUpActions(!showScheduleFollowUpActions);
  };

  const handleScheduleFollowUpFormSubmit =
    (action: LeadUpdateActions) => (formData: ObjectLiteral) => {
      handleLeadUpdateAction(action, formData);
    };

  const handleCloseStopContactDialog = () => {
    setOpenStopContactDialog(false);
  };

  const handleStopContact = () => {
    handleLeadUpdateAction(LeadUpdateActions.STOP_CONTACT);
  };

  const handleLeadUpdateAction = (action: LeadUpdateActions, data: ObjectLiteral = {}) => {
    if (!lead.id) return;

    if (openScheduleFollowUpDialog) {
      closeScheduleFollowUpDialog();
    }

    if (showScheduleFollowUpActions) {
      setShowScheduleFollowUpActions(false);
    }

    if (openStopContactDialog) {
      handleCloseStopContactDialog();
    }

    if (action === LeadUpdateActions.SCHEDULE_FOLLOW_UP) {
      if (data.icon === CommunicationTypes.CALL) {
        // TODO: these logic should not be needed once we also route sms followup to comm-api
        const scheduleData = omit(data, ['followUpMessage', 'icon']) as ScheduleCallPayload;

        Object.assign(scheduleData, {
          message: data.followUpMessage,
        });

        dispatch(
          apiScheduleCall({
            leadId: lead.id,
            data: scheduleData,
          })
        );
        return;
      }
    }

    if (action === LeadUpdateActions.SCHEDULE_CALL_IMMEDIATELY) {
      const scheduleData = {
        immediate: true,
        timezone: lead.timezone,
      } as ScheduleCallPayload;

      dispatch(
        apiScheduleCall({
          leadId: lead.id,
          data: scheduleData,
        })
      );
      return;
    }

    switch (action) {
      case LeadUpdateActions.GOOD_EMAIL_BAD_PHONE:
        dispatch(apiInvalidPhoneLead({ reason: AppCopyText.BAD_PHONE_REASON, autoDone: true }));
        break;
      case LeadUpdateActions.SCHEDULE_FOLLOW_UP:
        dispatch(apiCreateScheduledFollowUp(data));
        break;
      case LeadUpdateActions.NO_RESPONSE_NEEDED:
        dispatch(apiNoResponseNeeded());
        break;
      case LeadUpdateActions.CONTINUE_AUTOMATION:
        dispatch(apiContinueAutomation());
        break;
      case LeadUpdateActions.ESCALATE_LEAD:
        dispatch(apiEscalateLead());
        break;
      case LeadUpdateActions.STOP_CONTACT:
        dispatch(apiStopContactLead());
        break;
      default:
        break;
    }
  };

  const handleDeleteScheduledLead = () => {
    dispatch(apiDeleteScheduledFollowUp());
    setShowScheduleFollowUpActions(false);
  };

  const handleActionButtonClick = (action: LeadUpdateActions) => () => {
    if (action === LeadUpdateActions.STOP_CONTACT) {
      setOpenStopContactDialog(true);
    } else {
      handleLeadUpdateAction(action);
    }
  };

  const hotKeyHandlers = {
    CONTINUE_AUTO: () => handleLeadUpdateAction(LeadUpdateActions.CONTINUE_AUTOMATION),
  };

  return (
    <div>
      <LeadActionsWrapper>
        <LeadActionButton
          title="Continue Automation"
          icon={<SyncIcon />}
          onClick={handleActionButtonClick(LeadUpdateActions.CONTINUE_AUTOMATION)}
          disabled={conciergeIsOnCall || disallowContinueAuto}
        />
        {!conciergeIsOnCall && <GlobalHotKeys handlers={hotKeyHandlers} />}
        <LeadActionButton
          title="No Response Needed"
          icon={<SpeakerNotesOffIcon />}
          onClick={handleActionButtonClick(LeadUpdateActions.NO_RESPONSE_NEEDED)}
          disabled={conciergeIsOnCall || disallowNoResponseNeeded}
        />
        <LeadActionButton
          title="Good Email/Bad Phone"
          icon={<MarkEmailReadIcon className={classes.customSvgIcon} />}
          onClick={handleActionButtonClick(LeadUpdateActions.GOOD_EMAIL_BAD_PHONE)}
          disabled={conciergeIsOnCall}
        />
        <div className={cx(classes.chipStackWrapper, 'full-width')}>
          <div ref={scheduleFollowUpTrigger}>
            <LeadActionButton
              title={!!lead.phone ? 'Schedule Follow-Up' : 'Lead does not have a phone number'}
              icon={<EventIcon />}
              showBadge={!!scheduledLeadType}
              onClick={toggleScheduleFollowUpActions}
              disabled={!lead.phone}
            />
          </div>

          <Menu
            anchorEl={scheduleFollowUpTrigger.current}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            open={showScheduleFollowUpActions}
            onClose={() => setShowScheduleFollowUpActions(false)}
          >
            <MenuItem
              disabled={!canCallNow}
              onClick={handleActionButtonClick(LeadUpdateActions.SCHEDULE_CALL_IMMEDIATELY)}
            >
              schedule call immediately
            </MenuItem>
            <MenuItem onClick={openScheduleFollowUpFormDialog(ScheduleFollowUpTimeFrames.TOMORROW)}>
              follow-up tomorrow
            </MenuItem>
            <MenuItem
              onClick={openScheduleFollowUpFormDialog(ScheduleFollowUpTimeFrames.NEXT_WEEK)}
            >
              follow-up next week
            </MenuItem>
            <MenuItem
              onClick={openScheduleFollowUpFormDialog(ScheduleFollowUpTimeFrames.NEXT_MONTH)}
            >
              follow-up next month
            </MenuItem>
            <Divider />
            <MenuItem onClick={openScheduleFollowUpFormDialog(ScheduleFollowUpTimeFrames.CUSTOM)}>
              follow-up at a specific time
            </MenuItem>
            {!!scheduledLeadType && (
              <>
                <Divider />
                <MenuItem onClick={handleDeleteScheduledLead}>cancel {scheduledLeadType}</MenuItem>
              </>
            )}
          </Menu>

          <Popper
            anchorEl={scheduleFollowUpTrigger.current}
            placement="bottom-start"
            open={openScheduleFollowUpDialog}
          >
            <Paper>
              <ScheduleFollowUpForm
                onClose={closeScheduleFollowUpDialog}
                onSubmit={handleScheduleFollowUpFormSubmit(LeadUpdateActions.SCHEDULE_FOLLOW_UP)}
                timeFrame={scheduleFollowUpTimeFrame}
              />
            </Paper>
          </Popper>
        </div>
        {conciergeLdeIsLimited && (
          <LeadActionButton
            title="Escalate Lead"
            icon={<WarningRoundedIcon />}
            onClick={handleActionButtonClick(LeadUpdateActions.ESCALATE_LEAD)}
          />
        )}
        {/*FIXME: The icon mentioned in the ticket is only available in material UI v4. We're on v3 and would need to upgrade React to get to v4.*/}
        <LeadActionButton
          title="Stop Contact"
          icon={
            <svg
              className="MuiSvgIcon-root"
              focusable="false"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M15.73 3H8.27L3 8.27v7.46L8.27 21h7.46L21 15.73V8.27L15.73 3zm.51 11.83-1.41 1.41L12 13.41l-2.83 2.83-1.41-1.41L10.59 12 7.76 9.17l1.41-1.41L12 10.59l2.83-2.83 1.41 1.41L13.41 12l2.83 2.83z"></path>
            </svg>
          }
          onClick={handleActionButtonClick(LeadUpdateActions.STOP_CONTACT)}
        />
        <StopContactDialog
          open={openStopContactDialog}
          onSubmit={handleStopContact}
          onCancel={handleCloseStopContactDialog}
        />
      </LeadActionsWrapper>
    </div>
  );
};

export default LeadActions;
